/**
 * Mobility MVP: Pelvic Tilt Success Screen
 * Success state after pelvic tilt assessment
 * Follows FIGMA success pattern
 */

import { CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface PelvicTiltSuccessProps {
  result: 'good_control' | 'limited_control' | 'no_control';
  details?: { evidence: string[]; confidence: number };
  onContinue: () => void;
}

const RESULT_LABELS: Record<string, string> = {
  good_control: 'Good Control',
  limited_control: 'Limited Control',
  no_control: 'No Control'
};

export function PelvicTiltSuccess({ result, details, onContinue }: PelvicTiltSuccessProps) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-[420px] w-full">
        <CardContent className="pt-6 pb-6">
          <div className="flex flex-col items-center text-center space-y-6">
            {/* Success icon */}
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>

            {/* Result summary */}
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold">Assessment Complete</h2>
              <Badge variant="outline" className="text-base px-4 py-2">
                {RESULT_LABELS[result] || result}
              </Badge>
              {typeof details?.confidence === 'number' && (
                <p className="text-xs text-muted-foreground">
                  Confidence: {Math.round(details.confidence * 100)}%
                </p>
              )}
            </div>

            {details?.evidence?.length ? (
              <div className="w-full text-left">
                <p className="text-sm font-medium mb-2">What we saw</p>
                <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                  {details.evidence.slice(0, 5).map((e, idx) => (
                    <li key={idx}>{e}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* Continue button */}
            <Button onClick={onContinue} className="w-full" size="lg">
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
